import { alertError } from '../alert';
import { REPORT_TYPES } from '../../../components/tables/reports/consts';
import { DOWNLOAD_REPORT_FINISHED } from '../../../constants/analytics';
import { SET_DOWNLOAD_REPORT_STATUS, ANALYTICS_LOADING } from '../../reducers/analytics';
import {
  salesByChannelReportColumns,
  salesByProductReportColumns,
  warehouseReportColumns,
  returnsReportColumns,
  cancellationsReportColumns,
  rlrsReportColumns,
} from '../../../components/tables/reports/reportsColumnNames';
import { fetchReport } from '../../../utils/analytics-export';
import { getOrderTypeMetadata } from '../../../components/pages/Analytics/hooks/useShouldDisplayOrderTypeFilter';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';

export const AnalyticsReportType = {
  SALES_BY_CHANNEL: 'sales_by_channel',
  SALES_BY_PRODUCT: 'sales_by_product',
  SALES_BY_WAREHOUSE: 'sales_by_warehouse',
  ORDER_RETURNS: 'order_returns',
  ORDER_CANCELLATIONS: 'order_cancellations',
  RLRS: 'rlrs',
};

const columnsByServerType = {
  [AnalyticsReportType.SALES_BY_CHANNEL]: salesByChannelReportColumns,
  [AnalyticsReportType.SALES_BY_PRODUCT]: salesByProductReportColumns,
  [AnalyticsReportType.SALES_BY_WAREHOUSE]: warehouseReportColumns,
  [AnalyticsReportType.ORDER_RETURNS]: returnsReportColumns,
  [AnalyticsReportType.ORDER_CANCELLATIONS]: cancellationsReportColumns,
  [AnalyticsReportType.RLRS]: rlrsReportColumns,
};

const frontendTypeByServerType = {
  [AnalyticsReportType.SALES_BY_CHANNEL]: REPORT_TYPES.SALES,
  [AnalyticsReportType.SALES_BY_PRODUCT]: REPORT_TYPES.SALES_BY_PRODUCT,
  [AnalyticsReportType.SALES_BY_WAREHOUSE]: REPORT_TYPES.SALES_BY_WAREHOUSE,
  [AnalyticsReportType.ORDER_RETURNS]: REPORT_TYPES.RETURNS,
  [AnalyticsReportType.ORDER_CANCELLATIONS]: REPORT_TYPES.CANCELLATIONS,
  [AnalyticsReportType.RLRS]: REPORT_TYPES.RLRS,
};

const getColumnLabel = (column, reportServerType) => {
  if (reportServerType) {
    return columnsByServerType[reportServerType][column].label;
  }

  return column.label;
};

export const exportReport =
  ({ reportType, onlySelectedColumns, byDateType, omitFilters = [] }) =>
  async (dispatch, getState) => {
    dispatch({ type: ANALYTICS_LOADING, payload: { exportModal: true } });

    const state = getState();
    const params = fetchReportParams({ state, reportType, onlySelectedColumns, byDateType, omitFilters });

    try {
      await fetchReport(params);
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: ANALYTICS_LOADING, payload: { exportModal: false } });
      dispatch({ type: SET_DOWNLOAD_REPORT_STATUS, payload: { status: DOWNLOAD_REPORT_FINISHED } });
    }
  };

function fetchReportParams({ state, reportType, onlySelectedColumns, byDateType, omitFilters = [] }) {
  const { supplierId } = state.role;
  const supplierRetailers = state.role.supplier.supplier_retailers;

  const analyticsRetailers = state.analytics.retailers;
  const analyticsRetailerIds = supplierRetailerUtils
    .setRetailersActiveStatus(analyticsRetailers)
    .filter(({ active }) => active)
    .map((retailer) => retailer.id);

  const retailers = supplierRetailers
    .map(({ retailer }) => retailer)
    .filter((retailer) => analyticsRetailerIds.includes(retailer.id));

  const { retailerIdByOrderType } = getOrderTypeMetadata(retailers);

  const {
    currentCurrency,
    filters: { dates, byDateType: byDateTypeFilter, retailerIds, warehouseIds, countries, orderTypes },
    reports,
  } = state.analytics;

  const requestedColumns = getRequestedColumns(reportType, reports, onlySelectedColumns);
  const reportFrontendType = frontendTypeByServerType[reportType];
  const groupBy = reports[reportFrontendType].dateType;
  const columnFilters = reports[reportFrontendType].filters;

  const params = {
    supplier_id: supplierId,
    report_type: reportType,
    start_date: dates.start.toISOString(),
    end_date: dates.end.toISOString(),
    client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    date_type: byDateType ?? byDateTypeFilter,
    currency: currentCurrency,
    retailer_ids: retailerIds,
    warehouse_ids: warehouseIds,
    country_codes: countries,
    columns: requestedColumns,
    group_by: groupBy,
    filters: columnFilters,
  };

  if (orderTypes.length === 1) {
    const retailerIdsForChosenOrderType = retailerIdByOrderType[orderTypes[0]];
    params.retailer_ids = retailerIds.filter((retailerId) => retailerIdsForChosenOrderType.includes(retailerId));
  }

  if (omitFilters?.length) {
    omitFilters.forEach((filter) => delete params[filter]);
  }

  return params;
}

function getRequestedColumns(reportType, reports, onlySelectedColumns) {
  const reportFrontendType = frontendTypeByServerType[reportType];
  if (!reportFrontendType) {
    return [];
  }

  const relevantColumns = columnsByServerType[reportType];
  const columnsToRequest = onlySelectedColumns
    ? Array.from(reports[reportFrontendType].visibleColumns)
    : Object.keys(relevantColumns);

  if (hasPriceColumns(columnsToRequest, relevantColumns)) {
    columnsToRequest.push('currency');
  }

  if (shouldAppendCountryColumn({ columnsToRequest, relevantColumns })) {
    columnsToRequest.push('country');
  }

  return columnsToRequest.map((columnKey) => getColumnLabel(columnKey, reportType));
}

function hasPriceColumns(columns, relevantColumns) {
  return columns.some((column) => relevantColumns[column].isCurrency);
}

function shouldAppendCountryColumn({ columnsToRequest = [], relevantColumns = {} }) {
  return !columnsToRequest.includes('country') && Object.keys(relevantColumns).includes('country');
}
